import { useMemo } from 'react'
import { useSelector } from 'react-redux'
import { skipToken } from '@reduxjs/toolkit/query'

import { useGetAcceptanceCategoriesQuery } from 'app/services/api/acceptanceCategories'
import { useBaselineInfoQuery } from 'app/services/api/baselines'
import { useGetActiveSystemScopeFilterProfileQuery } from 'app/services/api/filterprofiles'

import MapComponent from 'features/Map'
import {
  selectRiskUnit,
  selectSavedFilter
} from 'features/RiskManagement/Components/LocalFilter/localFilterSelectors'
import {
  buildRiskFilterQuery,
  buildRiskRenderer,
  RiskMapMode
} from 'features/RiskManagement/utils/mapRenderer'

interface IMapWrapper {
  baselineId: string
  continuousRouteId?: string
}

const MapWrapper = ({ baselineId, continuousRouteId }: IMapWrapper) => {
  const localFilter = useSelector(selectSavedFilter)
  const riskUnit = useSelector(selectRiskUnit)

  const { currentData: baseline } = useBaselineInfoQuery(
    baselineId ?? skipToken
  )
  const { systemScopeFilterProfile } =
    useGetActiveSystemScopeFilterProfileQuery()

  const { data: acceptanceCategories } = useGetAcceptanceCategoriesQuery()

  const continuousRoutes = systemScopeFilterProfile?.continuousRoutes

  const renderer = useMemo(
    () =>
      buildRiskRenderer({
        acceptanceCategories,
        riskUnit
      }),
    [acceptanceCategories, riskUnit]
  )

  const filterQuery = useMemo(
    () =>
      buildRiskFilterQuery({
        continuousRoutes,
        localFilter,
        acceptanceCategories,
        riskUnit,
        continuousRouteId,
        mode: RiskMapMode.QRA
      }),
    [
      continuousRoutes,
      continuousRouteId,
      localFilter,
      acceptanceCategories,
      riskUnit
    ]
  )

  const contentFields = [
    {
      fieldName: 'RouteName',
      label: 'Route Name'
    },
    {
      fieldName: 'FromMeasure',
      label: 'From Measure',
      format: {
        places: 3,
        digitSeparator: true
      }
    },
    {
      fieldName: 'ToMeasure',
      label: 'To Measure',
      format: {
        places: 3,
        digitSeparator: true
      }
    },
    {
      fieldName: 'FromDate',
      label: 'From Date'
    },
    {
      fieldName: 'ToDate',
      label: 'To Date'
    },
    {
      fieldName: 'RiskTotal',
      label: 'Total Risk',
      format: {
        places: 2,
        digitSeparator: true
      }
    },
    {
      fieldName: 'RiskDensity',
      label: 'Risk Density',
      format: {
        places: 2,
        digitSeparator: true
      }
    }
  ]

  return (
    <MapComponent
      featureLayerTitle='Risk Baseline'
      featureLayerUrl={baseline?.baselineServiceUrl}
      featureLayerFilterQuery={filterQuery}
      renderer={renderer}
      contentFields={contentFields}
      showStructureLayer
    />
  )
}

export default MapWrapper
